import React, { useMemo } from 'react';
import { S8MixedMedia, S8MixedMediaProps, MediaItem, MediaType, MilanGradientColors, MilanBackgroundColors, MilanTextColors, MilanHeadlineColors } from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';

type BuilderMediaItem = {
  date: string;
  src: string;
  videoSrc?: string;
  imageAltText: string;
  linkText: string;
  linkUrl: string;
  linkColor: string;
  title: string;
  titleColor: string;
};

type S8MixedMediaBuilderProps = Omit<S8MixedMediaProps, 'items' | 'backgroundColor' | 'backgroundGradient'> & {
  desktopPaddingTop?: PaddingSize;
  desktopPaddingBottom?: PaddingSize;
  mobilePaddingTop?: PaddingSize;
  mobilePaddingBottom?: PaddingSize;
  items: BuilderMediaItem[];
  backgroundColor?: string;
  backgroundGradient?: MilanGradientColors | 'None' | 'default';
  backgroundImage?: string;
  headlineText: string;
  headlineColor: string;
};

export function S8MixedMediaSection(props: S8MixedMediaBuilderProps) {
  const {
    items,
    desktopPaddingTop = 'large',
    desktopPaddingBottom = 'large',
    mobilePaddingTop = 'small',
    mobilePaddingBottom = 'small',
    backgroundColor,
    backgroundGradient,
    backgroundImage,
    headlineText,
    headlineColor,
    ...restProps
  } = props;

  const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
  const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

  const transformedItems: MediaItem[] = useMemo(() =>
    items?.map((item, index) => {
      const type: MediaType = item.videoSrc ? 'video' : 'advertorial';
      const mediaSrc = (type === 'video' ? item.videoSrc : item.src) || '';

      return {
        id: `media-item-${index}-${type}-${mediaSrc.split('/').pop()}`,
        date: item.date,
        src: mediaSrc,
        imageAltText: item.imageAltText,
        linkText: item.linkText,
        linkUrl: item.linkUrl,
        linkColor: item.linkColor as MilanTextColors,
        title: item.title,
        titleColor: item.titleColor as MilanHeadlineColors,
        type,
      };
    }) || [], [items]
  );

  const processedBackgroundColor = backgroundColor === 'None' ? 'White' : backgroundColor;
  const processedBackgroundGradient = backgroundGradient === 'None' ? 'default' : backgroundGradient;

  const modifiedProps = {
    ...restProps,
    items: transformedItems,
    headlineText,
    headlineColor,
    backgroundColor: (processedBackgroundColor || 'default') as MilanBackgroundColors,
    backgroundGradient: processedBackgroundGradient as MilanGradientColors,
    backgroundImage,
    className: `${topPaddingClasses} ${bottomPaddingClasses}`,
  };

  const backgroundColorClass = processBackgroundAndGradientColorMap({
    backgroundColor: (processedBackgroundColor as MilanBackgroundColors) || 'White',
    gradientColor: processedBackgroundGradient || 'None',
  });

  return (
    <div className={`${backgroundColorClass}`}>
      <S8MixedMedia {...modifiedProps} />
    </div>
  );
}
